import { useEffect, useMemo, useRef, useState, ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';
import {
  CoreBox,
  CoreCheckbox,
  CoreDialogActions,
  CoreFormControl,
  CoreFormControlLabel,
  CoreTypography,
} from '@youscience/core';
import { ApproveRecommendationRequest } from '@youscience/commonapp-service-common';
import { Backdrop, CircularProgress, Dialog, DialogContent } from '@mui/material';
import printJS from 'print-js';
import { Document, Page } from 'react-pdf';
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import { SwiperSlide } from 'swiper/react';
import { PageCallback } from 'react-pdf/dist/cjs/shared/types';
import { NavigationOptions } from 'swiper/types';

import ActionButton from '@components/ActionButton';
import MonthDayYearDatePicker from '@components/MonthDayYearDatePicker';

import { renderInfoItem } from '@pages/StudentDetailsPage/components/SchoolCard/components/SchoolCardHeader';

import useRecommenderWorkspaceStore from '@store/RecommenderWorkspaceStore';

import PrintIcon from '@images/print-icon.svg?react';
import DownloadIcon from '@images/download-icon.svg?react';

import {
  sxStyles,
  StyledDialogTitle,
  StyledToggle,
  StyledList,
  StyledPdf,
  StyledSwiper,
} from './TeacherEvaluationPdf.styles';

import { usePdfPrint } from '@hooks/usePdfPrint';

const INITIAL_WIDTH = 600;
const INITIAL_HEIGHT = 800;

const MIN_DATE = new Date();
const MAX_DATE = new Date();
MIN_DATE.setDate(MIN_DATE.getDate() - 1);
MAX_DATE.setDate(MAX_DATE.getDate() + 1);

interface TeacherEvaluationPdfProps {
  teacherFullName?: string;
  recommenderId?: number;
  isPreviewOpen: boolean;
  onClosePreview: () => void;
}

export const TeacherEvaluationPdf = (props: TeacherEvaluationPdfProps) => {
  const { isPreviewOpen, recommenderId, teacherFullName, onClosePreview } = props;

  const params = useParams();
  const navigationPrevRef = useRef<HTMLDivElement>(null);

  const navigationNextRef = useRef<HTMLDivElement>(null);

  const {
    applicantFormsAvailable,
    getTeacherPreviewPdf,
    rejectTeacherEvaluation,
    approveTeacherEvaluation,
    previewPdfURL,
    isLoadingPreview,
    isLoadingTeacherEvaluation,
  } = useRecommenderWorkspaceStore((store) => store);

  const { printPdf } = usePdfPrint();

  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageHeight, setPageHeight] = useState(INITIAL_HEIGHT);
  const [pageWidth, setPageWidth] = useState(INITIAL_WIDTH);

  const initialFormState = {
    isReviewed: false,
    isAffirmed: false,
    signedDate: '',
  };

  const [formState, setFormState] = useState<Partial<ApproveRecommendationRequest>>({ ...initialFormState });
  const [dateHelperText, setDateHelperText] = useState('');

  const applicantId = +params.id!;

  const studentInfo = applicantFormsAvailable?.applicantFormRequests?.find(
    (student) => student.applicantId === applicantId,
  );

  const updateFormValues = (fieldUpdates: Partial<ApproveRecommendationRequest>) => {
    setFormState((prevState) => ({
      ...prevState,
      ...fieldUpdates,
    }));
  };

  const handleBeforeInit = (swiper: Swiper) => {
    // eslint-disable-next-line no-param-reassign
    swiper.params.navigation = {
      ...(swiper.params.navigation as NavigationOptions),
      prevEl: navigationPrevRef.current,
      nextEl: navigationNextRef.current,
    };
  };

  const handleDateChange = (value: Date | null) => {
    if (value) {
      updateFormValues({
        signedDate: `${(value.getMonth() + 1).toString().padStart(2, '0')}/${value
          .getDate()
          .toString()
          .padStart(2, '0')}/${value.getFullYear()}`,
      });
    } else {
      updateFormValues({ signedDate: '' });
    }
  };

  const handleDateError = (fieldName: string, error: string | undefined) => {
    let errorText = '';

    if (error === 'minDate' || error === 'maxDate') {
      errorText = 'Please enter a date within the range of yesterday and tomorrow.';
    } else if (error === 'invalidDate') {
      errorText = 'Please enter a valid date';
    }

    setDateHelperText(errorText);
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }): void => {
    setNumPages(numPages);
  };

  const pdfFile = useMemo(() => {
    if (!previewPdfURL) return null;

    try {
      // For react-pdf v9, we need to use a data URI with the base64 prefix
      const dataUri = previewPdfURL.startsWith('data:application/pdf;base64,')
        ? previewPdfURL
        : `data:application/pdf;base64,${previewPdfURL}`;

      return dataUri;
    } catch (error) {
      return null;
    }
  }, [previewPdfURL]);

  const onPageLoadSuccess = (page: PageCallback) => {
    setPageHeight(page.height + 2);
    setPageWidth(page.width + 2);
  };

  const handleAgreementChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateFormValues({ [event.target.name]: event.target.checked });
  };

  const handleDownload = () => {
    if (!previewPdfURL) return;

    try {
      // Method 1: Try the binary conversion approach first
      try {
        // Create a binary blob from the base64 data
        const binaryString = atob(previewPdfURL);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        // Create a blob and URL for download
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = 'teacher_evaluation.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error downloading PDF:', error);

        // Method 2: Fall back to data URI approach if binary conversion fails
        const dataUri = previewPdfURL.startsWith('data:application/pdf;base64,')
          ? previewPdfURL
          : `data:application/pdf;base64,${previewPdfURL}`;

        const link = document.createElement('a');
        link.href = dataUri;
        link.download = 'teacher_evaluation.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error downloading PDF:', error);
    }
  };

  const handlePrint = () => {
    if (!previewPdfURL) return;
    printPdf(previewPdfURL, 'teacher_evaluation.pdf');
  };

  const handleRejectTeacherEvaluation = async () => {
    const successResult = await rejectTeacherEvaluation(applicantId, recommenderId!);

    if (successResult.success) {
      onClosePreview();
    }
  };

  const handleApproveTeacherEvaluation = async () => {
    const successResult = await approveTeacherEvaluation(applicantId, recommenderId!, formState);

    if (successResult.success) {
      onClosePreview();
    }
  };

  useEffect(() => {
    void getTeacherPreviewPdf(applicantId, recommenderId!);
  }, []);

  return (
    <Dialog
      open={isPreviewOpen}
      onClose={onClosePreview}
      fullScreen
      maxWidth={false}
      PaperProps={{
        style: { height: '100vh', bottom: '0', position: 'fixed' },
      }}
      hideBackdrop={true}
    >
      <StyledDialogTitle>
        <CoreTypography variant="subtitle2">Review and submit</CoreTypography>

        <CoreBox sx={sxStyles.dialogTitleButtons}>
          <StyledToggle onClick={handleDownload}>
            <DownloadIcon />
          </StyledToggle>

          <StyledToggle onClick={handlePrint}>
            <PrintIcon />
          </StyledToggle>
        </CoreBox>
      </StyledDialogTitle>

      <DialogContent>
        <CoreBox sx={sxStyles.dialogMain}>
          <Backdrop
            open={isLoadingPreview ?? false}
            invisible
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, position: 'absolute' }}
          >
            <CircularProgress />
          </Backdrop>

          {previewPdfURL ? (
            <CoreBox sx={sxStyles.dialogContent}>
              <CoreBox sx={sxStyles.dialogContentTitles}>
                <CoreTypography variant="h5" component="h4">
                  Teacher evaluation
                </CoreTypography>

                <CoreTypography sx={sxStyles.greySubtitle}>
                  Please review the PDF preview of teacher evaluation
                </CoreTypography>

                <CoreBox sx={{ display: 'flex' }}>
                  {renderInfoItem('Submitted for:', studentInfo?.legalName ?? '')}

                  <CoreBox sx={sxStyles.divider} />

                  {renderInfoItem('Submitted by:', teacherFullName ?? '')}
                </CoreBox>
              </CoreBox>

              <CoreBox sx={sxStyles.dialogContentPdf}>
                <StyledPdf isLoading={numPages === 0} sx={{ height: pageHeight, width: pageWidth }}>
                  <Document
                    file={pdfFile}
                    onLoadSuccess={onDocumentLoadSuccess}
                    error={
                      <CoreBox sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 3 }}>
                        <CoreTypography color="error" gutterBottom>
                          Failed to load PDF directly. Please try downloading the file instead.
                        </CoreTypography>
                        <ActionButton
                          onClick={handleDownload}
                          startIcon={<DownloadIcon />}
                          variant="contained"
                          sx={{ mt: 2 }}
                        >
                          Download PDF
                        </ActionButton>
                      </CoreBox>
                    }
                    noData={<CoreTypography>No PDF data available.</CoreTypography>}
                    loading={<CircularProgress />}
                  >
                    <Page
                      onLoadSuccess={onPageLoadSuccess}
                      pageNumber={pageNumber}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                    />
                  </Document>
                </StyledPdf>

                <StyledSwiper
                  slidesPerView={1}
                  slidesPerGroup={1}
                  pagination={{ clickable: true }}
                  navigation={{
                    nextEl: navigationNextRef.current,
                    prevEl: navigationPrevRef.current,
                  }}
                  modules={[Pagination, Navigation]}
                  onBeforeInit={handleBeforeInit}
                  onSlideChange={(swiperCore) => {
                    setPageNumber(swiperCore.activeIndex + 1);
                  }}
                >
                  {numPages
                    ? Array.from({ length: numPages }, (_, i) => i).map((page) => (
                        <SwiperSlide key={page}>
                          <CoreBox sx={{ width: '0' }}></CoreBox>
                        </SwiperSlide>
                      ))
                    : null}
                </StyledSwiper>
              </CoreBox>

              <CoreBox sx={sxStyles.dialogContentAgreement}>
                <CoreTypography sx={sxStyles.greySubtitle} variant="body2">
                  Please affirm the following before you submit your form.
                </CoreTypography>

                <CoreFormControlLabel
                  name="isReviewed"
                  label={
                    <CoreTypography variant="body2">
                      I have reviewed the PDF copy of the form and I wish to proceed with submission.
                    </CoreTypography>
                  }
                  control={<CoreCheckbox value onChange={handleAgreementChange} />}
                />

                <CoreFormControlLabel
                  name="isAffirmed"
                  label={<CoreTypography variant="body2"> By signing and submitting this form,</CoreTypography>}
                  control={<CoreCheckbox value onChange={handleAgreementChange} />}
                />

                <StyledList>
                  <li>
                    <CoreTypography variant="body2">
                      I affirm that the form and all accompanying documents are not customized for a particular
                      institution.
                    </CoreTypography>
                  </li>

                  <li>
                    <CoreTypography variant="body2">
                      I understand that after I submit, I cannot edit or revoke my submission.
                    </CoreTypography>
                  </li>

                  <li>
                    <CoreTypography variant="body2">
                      I understand that these forms are available to every college that this student applied to using
                      Common App.
                    </CoreTypography>
                  </li>
                </StyledList>

                <CoreFormControl sx={sxStyles.dialogContentDate} fullWidth>
                  <MonthDayYearDatePicker
                    name="signedDate"
                    label="Date"
                    required
                    slotProps={{ inputProps: { clearable: true, onClear: () => handleDateChange } }}
                    value={formState.signedDate}
                    error={dateHelperText}
                    handleDateChange={handleDateChange}
                    handleDateError={handleDateError}
                    minDate={MIN_DATE}
                    maxDate={MAX_DATE}
                  />
                </CoreFormControl>
              </CoreBox>
            </CoreBox>
          ) : null}
        </CoreBox>
      </DialogContent>

      <CoreDialogActions data-testid="dialogFooter" sx={sxStyles.footer}>
        <ActionButton data-testid="cancelButton" variant="text" onClick={onClosePreview}>
          Cancel
        </ActionButton>

        <ActionButton
          data-testid="rejectButton"
          size="large"
          variant="outlined"
          onClick={handleRejectTeacherEvaluation}
          busyText="Rejecting..."
          disabled={isLoadingTeacherEvaluation}
        >
          Reject submission
        </ActionButton>

        <ActionButton
          data-testid="submitAllBtn"
          size="large"
          onClick={handleApproveTeacherEvaluation}
          disabled={isLoadingTeacherEvaluation || Object.values(formState).some((val) => !val) || !!dateHelperText}
          busyText="Submitting..."
        >
          Submit to all colleges
        </ActionButton>
      </CoreDialogActions>
    </Dialog>
  );
};
